import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import webBannerService from '../../services/webBannerService';
import { formatDateTime, getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Modal from '../../components/Common/Modal';
import MessageComponent from '../../components/Common/MessageComponent';
import { PAGES, STATUSES, extractErrors, pageMeta, placementLabel, statusMeta } from './constants';

const groupKey = (b) => `${b.page}|${b.displayType}|${b.slot}|${b.anchorSectionId || ''}`;

const WebBannersIndex = () => {
  const { isRtl } = useLocalization();
  const tr = (en, ar) => (isRtl ? ar : en);
  const locale = isRtl ? 'ar' : 'en';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState('');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [toast, setToast] = useState({ visible: false, type: 'success', message: '' });

  const notify = (type, message) => setToast({ visible: true, type, message });

  const load = useCallback(async () => {
    try {
      setItems(await webBannerService.list({ page: page || undefined, status, search: search.trim() || undefined }));
    } catch (e) {
      notify('error', extractErrors(e, tr('Failed to load banners.', 'تعذر تحميل البانرات.')).messages[0]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  // Banners that share a screen + position rotate as a carousel, in this order.
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((b) => {
      const k = groupKey(b);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(b);
    });
    return map;
  }, [items]);

  const move = async (b, dir) => {
    const list = groups.get(groupKey(b));
    const i = list.findIndex((x) => x.id === b.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    try {
      await webBannerService.reorder(next.map((x, idx) => ({ bannerId: x.id, sortOrder: idx + 1 })));
      await load();
    } catch (e) {
      notify('error', extractErrors(e, tr('Could not save the order.', 'تعذر حفظ الترتيب.')).messages[0]);
    }
  };

  const toggle = async (b) => {
    try {
      await webBannerService.setActive(b.id, !b.isActive);
      await load();
    } catch (e) {
      notify('error', extractErrors(e, tr('Update failed.', 'فشل التحديث.')).messages[0]);
    }
  };

  const duplicate = async (b) => {
    try {
      await webBannerService.duplicate(b.id);
      notify('success', tr('Duplicated as an inactive copy.', 'تم النسخ كنسخة غير مفعّلة.'));
      await load();
    } catch (e) {
      notify('error', extractErrors(e, tr('Duplicate failed.', 'فشل النسخ.')).messages[0]);
    }
  };

  const confirmDelete = async () => {
    const b = toDelete;
    setToDelete(null);
    try {
      await webBannerService.remove(b.id);
      notify('success', tr('Banner deleted.', 'تم حذف البانر.'));
      await load();
    } catch (e) {
      notify('error', extractErrors(e, tr('Delete failed.', 'فشل الحذف.')).messages[0]);
    }
  };

  const byPage = PAGES.map((p) => ({ page: p, rows: items.filter((b) => b.page === p.value) })).filter((g) => g.rows.length);
  const label = (b) => b.name || (isRtl ? b.titleAr : b.titleEn) || '—';
  const schedule = (b) =>
    !b.startsAtUtc && !b.endsAtUtc
      ? tr('Always', 'دائماً')
      : `${b.startsAtUtc ? formatDateTime(b.startsAtUtc, locale) : '…'} → ${b.endsAtUtc ? formatDateTime(b.endsAtUtc, locale) : '…'}`;

  if (loading) return <Loading text={tr('Loading banners...', 'جاري تحميل البانرات...')} />;

  return (
    <div className="cs-page">
      <MessageComponent message={toast.message} type={toast.type} isVisible={toast.visible} onClose={() => setToast((t) => ({ ...t, visible: false }))} />

      <div className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">{tr('Website Banners', 'بانرات الموقع')}</h1>
          <p className="page-description">
            {tr(
              'Banners and popups for the website: pick the image, text and button, then choose the screen and exact position. Several banners in one position rotate as a carousel.',
              'بانرات ونوافذ منبثقة للموقع: اختر الصورة والنص والزر ثم الصفحة والموضع بدقة. عدة بانرات في نفس الموضع تتحول إلى شريط متحرك.',
            )}
          </p>
        </div>
        <div className="page-header-actions">
          <Link to="/web-banners/new" className="btn btn-primary">+ {tr('New banner', 'بانر جديد')}</Link>
        </div>
      </div>

      <div className="card cs-toolbar">
        <input className="form-input" type="search" placeholder={tr('Search by name or text…', 'ابحث بالاسم أو النص…')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="form-select" value={page} onChange={(e) => setPage(e.target.value)}>
          <option value="">{tr('All screens', 'كل الصفحات')}</option>
          {PAGES.map((p) => (
            <option key={p.value} value={p.value}>{isRtl ? p.ar : p.en}</option>
          ))}
        </select>
        <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">{tr('All statuses', 'كل الحالات')}</option>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{isRtl ? s.ar : s.en}</option>
          ))}
        </select>
      </div>

      {items.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-title">{tr('No banners yet', 'لا توجد بانرات بعد')}</div>
          <p className="empty-description">{tr('Create a banner to promote an offer on the website.', 'أنشئ بانراً للترويج لعرض على الموقع.')}</p>
          <Link to="/web-banners/new" className="btn btn-primary">+ {tr('New banner', 'بانر جديد')}</Link>
        </div>
      ) : (
        byPage.map(({ page: p, rows }) => (
          <div className="cs-zone" key={p.value}>
            <div className="cs-zone-head">
              <h2>{isRtl ? pageMeta(p.value).ar : pageMeta(p.value).en}</h2>
              <span className="badge badge-neutral">{rows.length}</span>
            </div>
            <ul className="cs-rows">
              {rows.map((b) => {
                const st = statusMeta(b.status);
                const g = groups.get(groupKey(b)) || [];
                const idx = g.findIndex((x) => x.id === b.id);
                const rotating = b.displayType === 0 && g.length > 1;
                const src = getProductImageUrl(b.imageUrl);
                return (
                  <li className="cs-row wb-row" key={b.id}>
                    <div className="wb-thumb">{src ? <img src={src} alt="" loading="lazy" /> : null}</div>
                    <div className="cs-row-main">
                      <Link to={`/web-banners/${b.id}`} className="cs-row-title">{label(b)}</Link>
                      <div className="cs-row-sub">{placementLabel(b, isRtl)}</div>
                    </div>
                    <div className="cs-row-cells">
                      <span className={`badge ${st.badge}`}>{isRtl ? st.ar : st.en}</span>
                      <span className="badge badge-primary">{b.displayType === 1 ? tr('Popup', 'منبثق') : b.style === 1 ? tr('Image + text', 'صورة ونص') : b.style === 2 ? tr('Tile', 'بلاطة') : tr('Banner', 'بانر')}</span>
                      {rotating ? <span className="cs-cell">{tr(`Carousel ${idx + 1}/${g.length}`, `شريط متحرك ${idx + 1}/${g.length}`)}</span> : null}
                      <span className="cs-cell cs-cell--wide">🗓 {schedule(b)}</span>
                    </div>
                    <div className="cs-row-actions">
                      {g.length > 1 ? (
                        <>
                          <button type="button" className="btn btn-ghost btn-icon" disabled={idx === 0} onClick={() => move(b, -1)} aria-label="Up">↑</button>
                          <button type="button" className="btn btn-ghost btn-icon" disabled={idx === g.length - 1} onClick={() => move(b, 1)} aria-label="Down">↓</button>
                        </>
                      ) : null}
                      <button type="button" className={`cs-switch ${b.isActive ? 'is-on' : ''}`} role="switch" aria-checked={b.isActive} aria-label={tr('Enable / disable', 'تفعيل / تعطيل')} onClick={() => toggle(b)} />
                      <Link to={`/web-banners/${b.id}`} className="btn btn-secondary btn-small">{tr('Edit', 'تعديل')}</Link>
                      <button type="button" className="btn btn-ghost btn-small" onClick={() => duplicate(b)}>{tr('Duplicate', 'نسخ')}</button>
                      <button type="button" className="btn btn-ghost btn-small cs-danger" onClick={() => setToDelete(b)}>{tr('Delete', 'حذف')}</button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}

      <Modal
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        title={tr('Delete banner?', 'حذف البانر؟')}
        size="small"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setToDelete(null)}>{tr('Cancel', 'إلغاء')}</Button>
            <Button variant="danger" onClick={confirmDelete}>{tr('Delete', 'حذف')}</Button>
          </>
        )}
      >
        <p>{tr('This removes', 'سيُزال')} <strong>{toDelete ? label(toDelete) : ''}</strong> {tr('from the website.', 'من الموقع.')}</p>
      </Modal>
    </div>
  );
};

export default WebBannersIndex;
